import React, { useState, useEffect } from 'react';
import type { ForexRateConversionProps, CurrencyOption } from '../../types/forex-conversion';
import { useForexRateConversion } from '../../hooks/useForexRateConversion';
import { SUPPORTED_CURRENCIES } from '../../types/forex-conversion';
import { formatWithCommas } from '../../../../shared/utils/numberUtils';
import './ForexRateConversion.css';

const ForexRateConversion: React.FC<ForexRateConversionProps> = ({
  isOpen,
  onClose,
  currentCurrency,
  currentCurrencyId,
  amount,
  onConversionComplete,
  previousConversion,
  isInbound,
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [conversionResult, setConversionResult] = useState<{
    amount: number;
    currency: string;
    currencyId: number;
  } | null>(null);

  const { convertCurrency, loading, error, clearError } = useForexRateConversion();

  // True only when a real cross-currency conversion has happened - a reopened plan
  // whose previous choice was just the original currency (no conversion) doesn't count
  const hasConverted = !!conversionResult && conversionResult.currency !== currentCurrency;

  // Initialize state when modal opens - use previous conversion if available
  useEffect(() => {
    if (isOpen) {
      if (previousConversion) {
        // Pre-populate with previous conversion data
        setSelectedCurrency(previousConversion.currency);
        setConversionResult({
          amount: previousConversion.amount,
          currency: previousConversion.currency,
          currencyId: previousConversion.currencyId
        });
      } else {
        // Reset state for new conversion
        setSelectedCurrency('');
        setConversionResult(null);
      }
      clearError();
    }
  }, [isOpen, previousConversion, clearError]);

  // Handle currency selection and trigger conversion
  const handleCurrencySelect = async (currencyCode: string) => {
    clearError();

    if (currencyCode === currentCurrency) {
      setSelectedCurrency(currencyCode);
      // Only reset the cached result if there isn't already a real conversion to
      // revert back to - preserves the "reverted" summary display when applicable
      if (!hasConverted) {
        setConversionResult({
          amount,
          currency: currentCurrency,
          currencyId: currentCurrencyId ?? 0
        });
      }
      return;
    }

    // Reuse the cached result if we already converted to this currency - keyed on
    // conversionResult rather than selectedCurrency so it still hits after the user
    // has reverted to the original currency and re-selects the same target again
    if (conversionResult && conversionResult.currency === currencyCode) {
      setSelectedCurrency(currencyCode);
      return;
    }

    setSelectedCurrency(currencyCode);
    setConversionResult(null);

    try {
      
      const result = await convertCurrency({
        currentCurrency,
        targetCurrency: currencyCode,
        amount
      });

      if (result.success) {
        setConversionResult({
          amount: result.convertedAmount,
          currency: result.convertedCurrency,
          currencyId: result.currencyId
        });
        // Note: No auto-close, user must click CONVERT button
      }
    } catch (err) {
      console.error('ForexRateConversion: Conversion failed:', err);
    }
  };

  // Handle CONVERT button click - confirm whichever currency is currently selected,
  // not always the cached conversionResult (which may hold the other, reverted-from currency)
  const handleConvertClick = () => {
    if (!conversionResult || !selectedCurrency) return;

    if (selectedCurrency === currentCurrency) {
      onConversionComplete(amount, currentCurrency, currentCurrencyId);
    } else {
      onConversionComplete(conversionResult.amount, conversionResult.currency, conversionResult.currencyId);
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedCurrency('');
    setConversionResult(null);
    clearError();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="forex-modal-overlay" onClick={handleOverlayClick}>
      <div className="forex-modal-content">
        {/* Modal Header */}
        <div className="forex-modal-header">
          <h2 className="forex-modal-title">Choose Your Preferred Currency</h2>
          <button 
            className="forex-modal-close" 
            onClick={handleClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Subtitle */}
        <p className="forex-modal-subtitle">
          Choose your payment currency. {!isInbound ? `${currentCurrency} is not supported.` : ''}
        </p>

        {/* Error Display */}
        {error && (
          <div className="forex-error-state">
            <p className="forex-error-text">{error}</p>
          </div>
        )}

        {/* Currency Options - Horizontal Layout */}
        <div className="forex-currency-options">
          {SUPPORTED_CURRENCIES.map((currency: CurrencyOption) => {
            return (
              <div
                key={currency.code}
                className={`forex-currency-option ${selectedCurrency === currency.code ? 'selected' : ''}`}
                onClick={() => !loading && handleCurrencySelect(currency.code)}
                style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                <div className="forex-currency-radio"></div>
                <div className="forex-currency-info">
                  <div className="forex-currency-code">{currency.code} ({currency.name})</div>
                </div>
              </div>
            );
          })}
        </div>


        {/* Conversion Result Display */}
        {conversionResult && selectedCurrency && !loading && hasConverted && (() => {
          const isReverted = selectedCurrency === currentCurrency;
          return (
            <div className="forex-conversion-display visible">
              <h3 className="forex-conversion-title">
                {isReverted
                  ? `${conversionResult.currency} to ${currentCurrency} Conversion:`
                  : `${currentCurrency} to ${selectedCurrency} Conversion:`}
              </h3>
              <div className="forex-conversion-row">
                {isReverted ? (
                  <>
                    <p className="forex-conversion-result">
                      {conversionResult.currency} {formatWithCommas(conversionResult.amount)}
                    </p>
                    <p className="forex-conversion-result">
                      → {currentCurrency} {formatWithCommas(amount)}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="forex-conversion-result">
                      {currentCurrency} {formatWithCommas(amount)}
                    </p>
                    <p className="forex-conversion-result">
                      → {selectedCurrency} {formatWithCommas(conversionResult.amount)}
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })()}
        {/* Loading State */}
        {loading && selectedCurrency && (
          <div className="forex-loading-state">
            <div className="forex-loading-spinner"></div>
            Converting currency...
          </div>
        )}

        {/* Info Note */}
        <div className="forex-info-note">
          <p className="forex-info-text">
            Note: The conversion will be based on today's currency exchange rate.
          </p>
        </div>

        {/* CONVERT Button */}
        <div className="forex-convert-button-container">
          <button
            className={`forex-convert-button ${!selectedCurrency || loading ? 'disabled' : 'enabled'}`}
            onClick={handleConvertClick}
            disabled={!selectedCurrency || loading}
          >
            CONVERT
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForexRateConversion;